import React, { useState } from 'react';
import { message } from 'antd';
import WorkWsieIcon from '../../../../components/ResultComponent/WorkWsieIcon';
import folderIcon from '../../../../assets/DasboardIcons/folder.svg';
import { useOfflineToggle } from '../../../../components/OfflineToggle/useOfflineToggle';

const Sidebar = ({ open, onToggle, search, onSearch, onNewDocument, onSharedWithMe, onSignatureDocument, activeItem, onActiveItemChange, onNewFolder, folders }) => {
  const [quickAccessOpen, setQuickAccessOpen] = useState(true);
  const [foldersOpen, setFoldersOpen] = useState(true);
  const { offline } = useOfflineToggle();

  // Server-backed actions can't run offline. Wrap their handlers so a click gives
  // clear feedback instead of silently doing nothing.
  const withOnline = (fn, verb) => () => {
    if (offline) {
      message.warning(`You're offline. Connect to the internet to ${verb}.`);
      return;
    }
    fn();
  };

  const OFFLINE_TITLE = 'Unavailable offline';
  // Nav-item classes: dimmed + not-allowed when disabled offline, otherwise the
  // normal active/hover styling.
  const navItemCls = (isActive, disabled) =>
    disabled
      ? 'flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-400 opacity-60 cursor-not-allowed'
      : `flex items-center gap-2 rounded-md px-2 py-2 text-sm cursor-pointer ${isActive ? 'bg-primary-blue text-white hover:bg-primary-blue hover:text-white' : 'text-gray-700 hover:bg-primary-blue hover:text-white'}`;

  return (
    <aside className={`${open ? 'w-[22rem]' : 'w-16'} transition-all duration-200 bg-surface text-sidebar-foreground h-[125vh] flex flex-col md:sticky md:top-0 ${open ? 'fixed z-30 inset-y-0 left-0 md:static' : 'hidden md:flex'}`}>
      {/* Fixed Header Section */}
      <div className="flex-shrink-0">
        {/* Header: match image spacing and layout, keep current sidebar background */}
        <div className={`${open ? 'px-6' : 'px-3'} py-3 flex items-center justify-between`}>
          {/* Logo: show full wordmark when open, stripes-only when collapsed */}
          <div className="flex items-center">
            {open ? (
              <div className="flex items-center text-gray-900" role="img" aria-label="Workwise">
                <WorkWsieIcon
                  logoOnly={false}
                  horizontal
                  width="195"
                  height="52"
                  textColor="currentColor"
                />
              </div>
            ) : (
              <WorkWsieIcon
                width="30"
                height="30"
                logoOnly={true}
                textColor="#000000"
              />
            )}
          </div>
          <button onClick={onToggle} className={`inline-flex ${open ? 'h-8 w-8' : 'h-6 w-6'} ml-2 items-center justify-center rounded-md hover:bg-gray-100`} aria-label={open ? "Collapse sidebar" : "Expand sidebar"}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className={`${open ? 'h-5 w-5' : 'h-4 w-4'} text-gray-700`}>
              {open ? (
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
              ) : (
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
              )}
            </svg>
          </button>
        </div>

        {open && (
          <>
            <div className="px-6 py-2 space-y-3">
              <button onClick={onNewDocument} className="w-full inline-flex items-center gap-2 rounded-md bg-primary-blue text-white px-3 py-2 hover:bg-primary-blue-hover">
                <span className="text-lg leading-none">+</span>
                <span className="text-sm font-medium">{offline ? 'Create Offline Document' : 'Create Document'}</span>
              </button>

              <div className="relative">
                <input
                  value={search}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder="Search documents..."
                  className="w-full rounded-md border border-gray-300 bg-surface pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                </svg>
              </div>

              <div className="border-b border-gray-200 -mx-6"></div>
            </div>
          </>
        )}
      </div>

      {/* Scrollable Content Section */}
      {open && (
        <div className="flex-1 overflow-y-auto scrollbar-hide px-6 pb-4">
          <nav className="pt-2">
            <div className="text-[11px] uppercase text-gray-500 mb-2 flex items-center justify-between">
              <span>Quick Access</span>
              <button onClick={() => setQuickAccessOpen(!quickAccessOpen)} className="h-4 w-4 inline-flex items-center justify-center hover:bg-gray-100 rounded">
                <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  {quickAccessOpen ? (
                    <path d="M6 9l6 6 6-6" />
                  ) : (
                    <path d="M9 6l6 6-6 6" />
                  )}
                </svg>
              </button>
            </div>
            {quickAccessOpen && (
              <ul className="space-y-1">
                <li>
                  <a onClick={() => onActiveItemChange('all-documents')} className={`flex items-center gap-2 rounded-md px-2 py-2 text-sm ${activeItem === 'all-documents' ? 'bg-primary-blue text-white hover:bg-primary-blue hover:text-white' : 'text-gray-700 hover:bg-primary-blue hover:text-white'}`}>
                    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                      <polyline points="14,2 14,8 20,8" />
                    </svg>
                    All Documents 
                  </a>
                </li>
                <li>
                  <a
                    onClick={withOnline(onSignatureDocument, 'view signature documents')}
                    title={offline ? OFFLINE_TITLE : undefined}
                    className={navItemCls(activeItem === 'signature', offline)}
                  >
                    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M12 19l7-7 3 3-7 7-3-3z" />
                      <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                      <path d="M2 2l7.586 7.586" />
                      <circle cx="11" cy="11" r="2" />
                    </svg>
                    Signature document
                  </a>
                </li>
                <li>
                  <a
                    onClick={withOnline(onSharedWithMe, 'open My Drive')}
                    title={offline ? OFFLINE_TITLE : undefined}
                    className={navItemCls(activeItem === 'my-drive', offline)}
                  >
                    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    My Drive
                  </a>
                </li>
              </ul>
            )}
          </nav>

          <button
            onClick={withOnline(onNewFolder, 'create folders')}
            title={offline ? OFFLINE_TITLE : undefined}
            className={`mt-3 w-full inline-flex items-center gap-2 rounded-md text-white px-3 py-2 ${offline ? 'bg-primary-blue/50 cursor-not-allowed' : 'bg-primary-blue hover:bg-primary-blue-hover'}`}
          >
            <span className="text-lg leading-none">+</span>
            <span className="text-sm font-medium">New Folder</span>
          </button>

          <div className="pt-4">
            <div className="text-[11px] uppercase text-gray-500 mb-2 flex items-center justify-between">
              <span>Folders</span>
              <button onClick={() => setFoldersOpen(!foldersOpen)} className="h-4 w-4 inline-flex items-center justify-center hover:bg-gray-100 rounded">
                <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  {foldersOpen ? (
                    <path d="M6 9l6 6 6-6" />
                  ) : (
                    <path d="M9 6l6 6-6 6" />
                  )}
                </svg>
              </button>
            </div>
            {foldersOpen && (
              <>
                <ul className="space-y-1">
                  {folders.map((folder) => (
                    <li key={folder.id}>
                      <a
                        onClick={withOnline(() => onActiveItemChange(folder.id), 'open folders')}
                        title={offline ? OFFLINE_TITLE : undefined}
                        className={navItemCls(activeItem === folder.id, offline)}
                      >
                        <img src={folderIcon} alt="Folder" className="h-4 w-4" />
                        {folder.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
      {!open && (
        <div className="px-2 py-3 flex flex-col items-center gap-2">
          {/* Collapsed icon-only quick actions to ensure visibility */}
          <div className="mt-2 flex flex-col items-center gap-1">
            {[
              { id: 'docs', name: 'Docs', title: 'Docs', icon: (<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25V15A2.25 2.25 0 006.75 17.25h9.879a2.25 2.25 0 001.591-.659l1.53-1.53a2.25 2.25 0 00.659-1.591z" /></svg>) },
              { id: 'sheets', name: 'Sheets', title: 'Sheets', icon: (<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25h16.5M3.75 9.75h16.5M3.75 14.25h16.5M3.75 18.75h16.5" /></svg>) },
              { id: 'slides', name: 'Slides', title: 'Slides', icon: (<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="6" width="18" height="12" rx="2" ry="2" strokeWidth="2" /><path d="M12 18v3" strokeWidth="2" /></svg>) },
              { id: 'vids', name: 'Vids', title: 'Vids', icon: (<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><polygon points="5,3 19,12 5,21 5,3" strokeWidth="2" /></svg>) },
              { id: 'forms', name: 'Forms', title: 'Forms', icon: (<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="4" y="4" width="16" height="16" rx="2" strokeWidth="2" /><path d="M8 8h8M8 12h8M8 16h5" strokeWidth="2" /></svg>) },
            ].map((item) => (
              <button key={item.id} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-primary-blue hover:text-white" title={item.title}>
                {item.icon}
              </button>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
